import React, { useMemo } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calculator, Eye, EyeOff, TrendingUp, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useOrgProfile, PRICING_DEFAULTS } from '@/hooks/useOrgSettings';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { type CostBase } from '@/utils/pricing/applyPricing';
import { computeEstimateCostBase, rowCostOptionsFrom } from '@/utils/pricing/computeEstimateCostBase';
import { applyPricingForRows } from '@/utils/pricing/applyPricingForRows';
import {
  mergeEstimatePricing,
  type EstimatePricingKey,
  type EstimatePricingOverrides,
} from '@/utils/pricing/mergeEstimatePricing';
import { useUpdateEstimatePricingOverrides } from '@/hooks/useUpdateEstimatePricingOverrides';
import type { EstimateStratigraphy } from '@/types/estimateStratigraphy';
import { Link } from 'react-router-dom';
import { formatNumber, formatEuro } from '@/utils/format';

interface SellingPriceCardProps {
  stratigraphies: (EstimateStratigraphy & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    stratigraphy?: any;
  })[];
  /** Preventivo: serve id + pricingOverrides per il pricing per-preventivo. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  estimate?: { id?: string; pricingOverrides?: EstimatePricingOverrides | null } | any;
}

function euro(v: number): string {
  // Delega al formattatore condiviso: prima faceva solo il replace del punto
  // con la virgola, quindi restava senza separatore delle migliaia (41316,91).
  return formatEuro(v);
}

function num(v: unknown, fb = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
}

/**
 * Campo percentuale inline (componente STABILE a livello di modulo per non
 * perdere il focus mentre si digita). Mostra il valore effettivo; owner/admin
 * lo editano creando un override per-preventivo, con reset al default org.
 */
const PctField: React.FC<{
  k: EstimatePricingKey;
  def: number;
  effVal: number;
  isOverridden: boolean;
  canEdit: boolean;
  onSet: (k: EstimatePricingKey, v: number | null) => void;
}> = ({ k, effVal, isOverridden, canEdit, onSet }) => {
  const [draft, setDraft] = React.useState<string>('');

  if (!canEdit) {
    return <span className="text-muted-foreground">({effVal}%)</span>;
  }
  const commit = () => {
    const raw = draft.replace(',', '.').trim();
    setDraft('');
    if (raw === '') return;
    const v = Number(raw);
    if (!Number.isFinite(v) || v < 0) return;
    if (Math.abs(v - effVal) < 0.001) return;
    onSet(k, v);
  };
  return (
    <span className="inline-flex items-center gap-0.5">
      (
      <Input
        type="text"
        inputMode="decimal"
        value={draft || String(effVal)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className={`h-6 w-12 px-1 text-center text-xs ${isOverridden ? 'border-amber-400 bg-amber-50 font-semibold' : ''}`}
        title={isOverridden ? 'Override per questo preventivo (↺ per tornare al default org)' : 'Default org — modifica per sovrascrivere solo qui'}
      />
      %
      {isOverridden && (
        <button
          type="button"
          onClick={() => onSet(k, null)}
          title="Ripristina default organizzazione"
          className="ml-0.5 text-amber-700 hover:text-amber-900"
        >
          <RotateCcw className="h-3 w-3" />
        </button>
      )}
      )
    </span>
  );
};

/**
 * Gemello di `PctField` per gli importi in euro (trasporto, smaltimento
 * forfait). Stesso comportamento: mostra il valore effettivo, owner/admin lo
 * sovrascrivono per questo preventivo, la freccia riporta al default org.
 */
const FlatField: React.FC<{
  k: EstimatePricingKey;
  effVal: number;
  isOverridden: boolean;
  canEdit: boolean;
  onSet: (k: EstimatePricingKey, v: number | null) => void;
}> = ({ k, effVal, isOverridden, canEdit, onSet }) => {
  const [draft, setDraft] = React.useState<string>('');

  if (!canEdit) {
    return <span className="text-muted-foreground">(€ {effVal})</span>;
  }
  const commit = () => {
    const raw = draft.replace(',', '.').trim();
    setDraft('');
    if (raw === '') return;
    const v = Number(raw);
    if (!Number.isFinite(v) || v < 0) return;
    if (Math.abs(v - effVal) < 0.001) return;
    onSet(k, v);
  };
  return (
    <span className="inline-flex items-center gap-0.5">
      (€
      <Input
        type="text"
        inputMode="decimal"
        value={draft || String(effVal)}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        className={`h-6 w-16 px-1 text-center text-xs ${isOverridden ? 'border-amber-400 bg-amber-50 font-semibold' : ''}`}
        title={isOverridden ? 'Override per questo preventivo (↺ per tornare al default org)' : 'Default org — modifica per sovrascrivere solo qui'}
      />
      {isOverridden && (
        <button
          type="button"
          onClick={() => onSet(k, null)}
          title="Ripristina default organizzazione"
          className="ml-0.5 text-amber-700 hover:text-amber-900"
        >
          <RotateCcw className="h-3 w-3" />
        </button>
      )}
      )
    </span>
  );
};

/**
 * Card "Vista vendita" per il preventivo (F29). Pipeline costo → prezzo vendita.
 * I markup/spese/IVA/sconto usano i default org, ma ogni preventivo puo'
 * SOVRASCRIVERLI: owner/admin editano le % inline e valgono solo per questo
 * preventivo (card + PDF). Visibile SOLO a owner/admin.
 */
const SellingPriceCard: React.FC<SellingPriceCardProps> = ({ stratigraphies, estimate }) => {
  const { currentRole } = useCurrentOrganization();
  const canSeeMargins = currentRole === 'owner' || currentRole === 'admin';
  const { data: org } = useOrgProfile();
  const [showDetails, setShowDetails] = React.useState(true);

  const overrides = (estimate?.pricingOverrides ?? {}) as EstimatePricingOverrides;
  const effective = useMemo(() => mergeEstimatePricing(org ?? null, overrides), [org, overrides]);

  const updateOverrides = useUpdateEstimatePricingOverrides(estimate?.id ?? '');
  const canEdit = canSeeMargins && !!estimate?.id;

  const setOverride = (key: EstimatePricingKey, value: number | null) => {
    const next: EstimatePricingOverrides = { ...overrides };
    if (value == null || !Number.isFinite(value)) delete next[key];
    else next[key] = value;
    updateOverrides.mutate(next);
  };

  // Helper: rende il campo percentuale per una chiave (PctField e' stabile a
  // livello di modulo → nessun remount/perdita focus).
  const flat = (k: EstimatePricingKey, def: number) => (
    <FlatField
      k={k}
      effVal={num((effective as Record<string, unknown>)[k], def)}
      isOverridden={overrides[k] != null}
      canEdit={canEdit}
      onSet={setOverride}
    />
  );

  const pct = (k: EstimatePricingKey, def: number) => (
    <PctField
      k={k}
      def={def}
      effVal={num((effective as Record<string, unknown>)[k], def)}
      isOverridden={overrides[k] != null}
      canEdit={canEdit}
      onSet={setOverride}
    />
  );

  const costBase = useMemo<CostBase>(
    () => computeEstimateCostBase(stratigraphies, rowCostOptionsFrom(effective)),
    [stratigraphies, effective],
  );

  const breakdown = useMemo(
    () => applyPricingForRows(costBase, effective, stratigraphies),
    [costBase, effective, stratigraphies],
  );

  if (!canSeeMargins) return null;
  if (stratigraphies.length === 0) return null;

  // Margine: lordo e netto, entrambi SUL RICAVO. Il calcolo vive in
  // applyPricing, cosi' card e barra riepilogo non possono divergere.
  const { marginGross, marginGrossPct, marginNet, marginNetPct } = breakdown;
  const hasAnyOverride = Object.keys(overrides ?? {}).length > 0;

  return (
    <Card className="border-primary/30 bg-gradient-to-br from-background to-primary/5">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <Calculator className="h-5 w-5 text-primary" />
            <div>
              <CardTitle className="flex items-center gap-2">
                Vista vendita (gestionale)
                <Badge variant="secondary" className="text-xs">Interno</Badge>
                {hasAnyOverride && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-800 border-amber-200">
                    Pricing personalizzato
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Pipeline costo → prezzo cliente. Percentuali editabili per questo preventivo.
              </CardDescription>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowDetails(s => !s)} className="gap-1">
            {showDetails ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showDetails ? 'Nascondi' : 'Mostra'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Highlight bar in alto */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-md border bg-background p-3">
            <p className="text-xs text-muted-foreground">Costo base</p>
            <p className="text-lg font-bold">{euro(breakdown.costBase)}</p>
          </div>
          <div className="rounded-md border bg-background p-3">
            <p className="text-xs text-muted-foreground">Prezzo netto (imp.)</p>
            <p className="text-lg font-bold text-primary">{euro(breakdown.netPrice)}</p>
          </div>
          <div
            className="rounded-md border bg-background p-3"
            title="Margine netto: ricavo meno TUTTI i costi, accessori inclusi. Le percentuali sono sul ricavo, non sul costo."
          >
            <p className="text-xs text-muted-foreground">Margine netto</p>
            <p className="text-lg font-bold text-green-600 flex items-center gap-1">
              <TrendingUp className="h-4 w-4" />
              {euro(marginNet)}
              <span className="text-xs font-normal text-muted-foreground">({formatNumber(marginNetPct, 1)}%)</span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-0.5 leading-tight">
              lordo {euro(marginGross)} ({formatNumber(marginGrossPct, 1)}%) &minus; accessori {euro(breakdown.accessoriesTotal)}
            </p>
          </div>
          <div className="rounded-md border bg-primary p-3 text-primary-foreground">
            <p className="text-xs opacity-80">Totale IVA inclusa</p>
            <p className="text-lg font-bold">{euro(breakdown.totalPrice)}</p>
          </div>
        </div>

        {/* Pipeline dettagliata */}
        {showDetails && (
          <div className="text-sm">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-2">
              {/* Costi diretti */}
              <div className="space-y-1">
                <h4 className="font-semibold text-xs text-muted-foreground mb-2 uppercase tracking-wide">Costi diretti (interno)</h4>
                <div className="flex justify-between"><span title="Materiali, viti, profilo perimetrale, sfrido e smaltimento: tutto ciò che si acquista.">Materiali</span><span className="font-mono">{euro(breakdown.costMaterials)}</span></div>
                {/* Sfrido e smaltimento sono DENTRO "Materiali + viti": qui si
                    mostrano solo per far vedere da dove arrivano, ed e' lo
                    stesso numero delle colonne in tabella. */}
                {(costBase.waste ?? 0) > 0 && (
                  <div className="flex justify-between text-xs text-muted-foreground pl-3">
                    <span>di cui sfrido</span>
                    <span className="font-mono">{euro(costBase.waste ?? 0)}</span>
                  </div>
                )}
                {(costBase.disposal ?? 0) > 0 && (
                  <div className="flex justify-between text-xs text-muted-foreground pl-3">
                    <span>di cui smaltimento materiali</span>
                    <span className="font-mono">{euro(costBase.disposal ?? 0)}</span>
                  </div>
                )}
                <div className="flex justify-between"><span>Manodopera</span><span className="font-mono">{euro(breakdown.costLabor)}</span></div>
                <div className="flex justify-between"><span>Finitura</span><span className="font-mono">{euro(breakdown.costFinish)}</span></div>
                <div className="flex justify-between border-t pt-1 font-semibold"><span>Costo base totale</span><span className="font-mono">{euro(breakdown.costBase)}</span></div>
              </div>

              {/* Mark-up */}
              <div className="space-y-1">
                <h4 className="font-semibold text-xs text-muted-foreground mb-2 uppercase tracking-wide">Mark-up applicati</h4>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">Su materiali {pct('markup_materials_pct', PRICING_DEFAULTS.markup_materials_pct)}</span>
                  <span className="font-mono">+ {euro(breakdown.markupMaterials)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">Su manodopera {pct('markup_labor_pct', PRICING_DEFAULTS.markup_labor_pct)}</span>
                  <span className="font-mono">+ {euro(breakdown.markupLabor)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">Su finitura {pct('markup_finish_pct', PRICING_DEFAULTS.markup_finish_pct)}</span>
                  <span className="font-mono">+ {euro(breakdown.markupFinish)}</span>
                </div>
                <div className="flex justify-between border-t pt-1 font-semibold"><span>Margine industriale</span><span className="font-mono text-green-600">+ {euro(breakdown.markupTotal)}</span></div>
              </div>

              {/* Accessori */}
              <div className="space-y-1">
                <h4 className="font-semibold text-xs text-muted-foreground mb-2 uppercase tracking-wide">Costi accessori</h4>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">Spese generali {pct('overhead_pct', PRICING_DEFAULTS.overhead_pct)}</span>
                  <span className="font-mono">+ {euro(breakdown.overhead)}</span>
                </div>
                {/* Sicurezza, trasporto e smaltimento erano calcolati e sommati
                    nel totale ma invisibili: la sicurezza non aveva alcuna riga,
                    trasporto e smaltimento comparivano solo se diversi da zero.
                    Ora si vedono sempre, anche a zero, e si modificano per il
                    singolo preventivo come i ricarichi. */}
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">Sicurezza / DPI {pct('safety_pct', PRICING_DEFAULTS.safety_pct)}</span>
                  <span className="font-mono">+ {euro(breakdown.safety)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">Trasporto {flat('transport_flat', PRICING_DEFAULTS.transport_flat)}</span>
                  <span className="font-mono">+ {euro(breakdown.transport)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span
                    className="flex items-center gap-1"
                    title="Costo fisso di cantiere IN PIÙ: container, viaggi in discarica. Lo smaltimento dei materiali è già conteggiato nei costi diretti, in percentuale sul loro costo."
                  >
                    Smaltimento a forfait (extra) {flat('disposal_flat', PRICING_DEFAULTS.disposal_flat)}
                  </span>
                  <span className="font-mono">+ {euro(breakdown.disposal)}</span>
                </div>
                <div className="flex justify-between border-t pt-1 font-semibold"><span>Totale accessori</span><span className="font-mono">+ {euro(breakdown.accessoriesTotal)}</span></div>
              </div>

              {/* Sconto + IVA */}
              <div className="space-y-1">
                <h4 className="font-semibold text-xs text-muted-foreground mb-2 uppercase tracking-wide">Sconto cliente & IVA</h4>
                <div className="flex justify-between"><span>Prezzo lordo</span><span className="font-mono">{euro(breakdown.grossPrice)}</span></div>
                <div className="flex justify-between items-center text-red-600">
                  <span className="flex items-center gap-1">Sconto cliente {pct('default_customer_discount_pct', PRICING_DEFAULTS.default_customer_discount_pct)}</span>
                  <span className="font-mono">{breakdown.customerDiscount > 0 ? `− ${euro(breakdown.customerDiscount)}` : euro(0)}</span>
                </div>
                <div className="flex justify-between font-semibold"><span>Imponibile</span><span className="font-mono">{euro(breakdown.netPrice)}</span></div>
                <div className="flex justify-between items-center">
                  <span className="flex items-center gap-1">IVA {pct('iva_pct', PRICING_DEFAULTS.iva_pct)}</span>
                  <span className="font-mono">+ {euro(breakdown.ivaAmount)}</span>
                </div>
                <div className="flex justify-between border-t pt-1 font-bold text-primary"><span>Totale offerta</span><span className="font-mono">{euro(breakdown.totalPrice)}</span></div>
              </div>
            </div>

            <div className="mt-4 pt-3 border-t text-xs text-muted-foreground">
              I valori di <strong>default</strong> si impostano in{' '}
              <Link to="/settings/organization" className="underline hover:text-foreground">Organizzazione</Link>
              {canEdit && <> · le percentuali qui sopra sono <strong>modificabili solo per questo preventivo</strong> (↺ = torna al default org)</>}
              . Prezzo finale cliente nel PDF: <strong>{euro(breakdown.totalPrice)}</strong>
              {' '}({euro(costBase.totalArea > 0 ? breakdown.netPrice / costBase.totalArea : 0)}/m² IVA esclusa).
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SellingPriceCard;
